// Iter 122 — Hub-based primary navigation.
// Just 5 top-level tabs: Dashboard · Clinical · Knowledge · Operations · Compliance
// (Staff users don't see Compliance.) Every sub-section is reached either
// via the hub landing tile grid, or via the ⌘K command palette.
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Stethoscope, BookOpen, Briefcase, Shield } from 'lucide-react';
import { SECTIONS, hubForPath } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const HUB_TABS = {
  dashboard:  { ...SECTIONS.dashboard,  icon: LayoutDashboard, href: '/' },
  clinical:   { ...SECTIONS.clinical,   icon: Stethoscope,     href: '/clinical' },
  knowledge:  { ...SECTIONS.knowledge,  icon: BookOpen,        href: '/knowledge' },
  operations: { ...SECTIONS.operations, icon: Briefcase,       href: '/operations' },
  compliance: { ...SECTIONS.compliance, icon: Shield,          href: '/compliance' },
};

// Display order — permissions decide which actually render.
const ALL_HUBS = ['dashboard', 'clinical', 'knowledge', 'operations', 'compliance'];

export default function SectionNav() {
  const location = useLocation();
  const { user } = useAuth();
  const { canHub, loaded } = usePermissions();

  // Until permissions load, show only Dashboard to avoid a flash of all tabs
  // for a brief moment then collapsing back.
  const hubs = (loaded ? ALL_HUBS.filter(canHub) : ['dashboard']).map(k => HUB_TABS[k]);

  const activeHub = hubForPath(location.pathname);

  if (!user) return null;

  return (
    <div className="bg-card border-b border-border" data-testid="section-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-1 py-1 overflow-x-auto">
          {hubs.map(t => {
            const Icon = t.icon;
            const isActive = activeHub === t.id;
            return (
              <Link key={t.id} to={t.href} data-testid={`section-tab-${t.id}`}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all border"
                style={isActive
                  ? { backgroundColor: t.colorBg, color: t.color, borderColor: t.ring, fontWeight: 600 }
                  : { borderColor: 'transparent' }}>
                <Icon className="w-4 h-4" strokeWidth={1.5}
                      style={{ color: isActive ? t.color : undefined }} />
                <span className={isActive ? '' : 'text-muted-foreground'}>{t.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
